import type { FlowContext } from '@/types'
import { z } from 'zod/v3'

type Validator = (value: string, ctx: FlowContext) => true | string

// ============================================
// Zod Schemas
// ============================================

export const schemas = {
  nome: z
    .string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Nome deve conter apenas letras'),

  idade: z.coerce
    .number({ message: 'Digite um número válido' })
    .int('Idade deve ser um número inteiro')
    .min(1, 'Idade deve ser pelo menos 1 ano')
    .max(119, 'Idade deve ser no máximo 119 anos'),

  altura: z.coerce
    .number({ message: 'Digite um número válido' })
    .min(0.5, 'Altura deve ser pelo menos 0.50 m')
    .max(2.5, 'Altura deve ser no máximo 2.50 m'),

  peso: z.coerce
    .number({ message: 'Digite um número válido' })
    .min(20, 'Peso deve ser pelo menos 20 kg')
    .max(300, 'Peso deve ser no máximo 300 kg'),

  textoLivre: z
    .string()
    .min(2, 'Digite pelo menos 2 caracteres')
    .max(500, 'Máximo de 500 caracteres'),

  email: z
    .string()
    .min(1, 'Informe seu e-mail')
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Digite um email válido'),

  telefone: z
    .string()
    .regex(
      /^(\+55\s?)?\(?\d{2}\)?\s?\d{4,5}[-\s]?\d{4}$/,
      'Digite um telefone válido (ex: 11 99999-9999)'
    ),
} as const

// ============================================
// Zod to Validator adapter
// ============================================

/** Creates a Validator function from a Zod schema */
export function fromZod<T extends z.ZodType>(schema: T): Validator {
  return (value: string) => {
    const result = schema.safeParse(value)
    if (result.success) return true
    return result.error.issues[0]?.message ?? 'Valor inválido'
  }
}

// ============================================
// Pre-built validators using Zod schemas
// ============================================

export const validateNome = fromZod(schemas.nome)
export const validateIdade = fromZod(schemas.idade)
export const validateAltura = fromZod(schemas.altura)
export const validatePeso = fromZod(schemas.peso)
export const validateTextoLivre = fromZod(schemas.textoLivre)
export const validateEmail = fromZod(schemas.email)
export const validateTelefone = fromZod(schemas.telefone)

// ============================================
// Legacy composable validators (for backwards compatibility)
// ============================================

/** Value must be a valid number */
export const isNumber =
  (msg = 'Digite um número válido'): Validator =>
  (value) =>
    !isNaN(Number(value)) && value.trim() !== '' ? true : msg

/** Number must be within [min, max] */
export const inRange =
  (min: number, max: number, msg?: string): Validator =>
  (value) => {
    const n = Number(value)
    if (isNaN(n)) return msg ?? `Valor deve ser entre ${min} e ${max}`
    return n >= min && n <= max ? true : (msg ?? `Valor deve ser entre ${min} e ${max}`)
  }

/** String must have at least n characters */
export const minLength =
  (n: number, msg?: string): Validator =>
  (value) =>
    value.trim().length >= n ? true : (msg ?? `Mínimo de ${n} caracteres`)

/** Compose multiple validators — returns first error or true */
export const compose =
  (...validators: Validator[]): Validator =>
  (value, ctx) => {
    for (const v of validators) {
      const result = v(value, ctx)
      if (result !== true) return result
    }
    return true
  }
